import React from 'react';
import { Typography, Box, Divider, Stack, Link as MuiLink } from '@mui/material';
import AcademiaItem from '../components/AcademiaItem';
import SOMCIS from '../assets/academia/Plataforma_Tecnologica_de_Inteligencia_Artificial.pdf';
import FNNDSC_Slides from '../assets/academia/Symposium_Slides.pdf';
import FNNDSC_Abstract from '../assets/academia/FNNDSC_Symposium_Abstract.pdf';
import FNNDSC_Certificate from '../assets/academia/Symposium_Certificate.pdf';
import highres_abstract from '../assets/academia/highres_abstract.pdf';
import highres_slides from '../assets/academia/highres_slides.pdf';
import AI_Cures_Poster from '../assets/academia/AI_Cures_Poster.jpeg';
import AI_in_Early_Healthcare from '../assets/academia/AI_in_Early_Healthcare_Anticipating_Challenges_and_Implications.pdf';
import Graph_Tec_Paper from '../assets/academia/Graph_Based_Embeddings_for_Analyzing_Competency_Gaps_in_Higher_Education_Paper.pdf';
import Graph_Tec_Poster from '../assets/academia/Graph_Based_Embeddings_for_Analyzing_Competency_Gaps_in_Higher_Education_Poster.pdf';

const publications = [
  {
    badge: 'Publication',
    title:
      'Conditional deep generative normative modeling for structural and developmental anomaly detection in the fetal brain',
    authors: [
      'Sungmin You',
      'Andrea Gondova',
      'Carlos Simon Amador Izaguirre',
      'Guillermo Tafoya Milo',
      'Seungyoon Jeong',
      'Han-Jui Lee',
      'Tomo Tarui',
      'Caitlin K. Rollins',
      'Hyuk Jin Yun',
      'P. Ellen Grant',
      'Kiho Im',
    ],
    eventOrSource: (
      <>
        <MuiLink
          href="https://doi.org/10.1016/j.neuroimage.2025.121442"
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
        >
          NeuroImage
        </MuiLink>
        , Volume 319, October 2025, Article 121442.
      </>
    ),
    date: 'Published Oct 1, 2025 | Epub Sep 3, 2025',
    doi: 'https://doi.org/10.1016/j.neuroimage.2025.121442',
    note:
      'Introduces a conditional generative normative framework for early detection of fetal brain anomalies.',
  },
];

const presentations = [
  {
    badge: 'Presentation',
    authors: [
      'Guillermo Tafoya Milo',
      'Sungmin You',
      'Carlos S. Amador Izaguirre',
      'Milton O. Candela Leal',
      'Hyuk Jin Yun',
      'P. Ellen Grant',
      'Kiho Im',
    ],
    title: 'Gestational Age-Conditioned Anomaly Detection in Fetal Brain MRI',
    eventOrSource: (
      <>
        Presented at the{' '}
        <MuiLink
          href="https://www.fnndsc.org/"
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
        >
          FNNDSC
        </MuiLink>
        , Boston Children&apos;s Hospital, Harvard Medical School Symposium, Boston, MA.
      </>
    ),
    date: 'March 20, 2024',
    slides: FNNDSC_Slides,
    certificate: FNNDSC_Certificate,
    document: FNNDSC_Abstract,
  },
  {
    badge: 'Presentation',
    authors: [
      'Milton O. Candela Leal',
      'Marisol Lemus Aguilar',
      'Enrique Mondragón Estrada',
      'Iqui Balam Heredia Marín',
      'Guillermo Tafoya Milo',
      'Jennings Zhang',
      'Hyuk Jin Yun',
      'P. Ellen Grant',
      'Kiho Im',
    ],
    title: 'High-Resolution Fetal Subplate Automatic Segmentation',
    eventOrSource:
      "Presented at the FNNDSC, Boston Children's Hospital, Harvard Medical School Symposium, Boston, MA.",
    date: 'March 20, 2024',
    slides: highres_slides,
    document: highres_abstract,
  },
  {
    badge: 'Presentation',
    authors: [
      'Sungmin You',
      'Guillermo Tafoya Milo',
      'Carlos S. Amador Izaguirre',
      'Seungyoon Jeong',
      'Hyuk Jin Yun',
      'P. Ellen Grant',
      'Kiho Im',
    ],
    title: 'Covariate-conditioned deep generative anomaly detection in fetal brain MRI with ventriculomegaly',
    eventOrSource: 'Presented at the MIT AI Cures Conference, Cambridge, MA.',
    date: 'April 1, 2024',
    poster: AI_Cures_Poster,
    media: 'https://www.mitmgb.ai/posters',
  },
];

const writtenWorks = [
  {
    badge: 'Essay',
    authors: ['Guillermo Tafoya Milo'],
    title: (
      <MuiLink
        href="https://datascienceincontext.com/ai-in-early-healthcare-anticipating-challenges-and-implications/"
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
      >
        AI in Early Healthcare: Anticipating Challenges and Implications
      </MuiLink>
    ),
    eventOrSource: (
      <>
        Published on Prof.{' '}
        <MuiLink
          href="https://www.amacad.org/person/alfred-z-spector"
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
        >
          Alfred Z. Spector
        </MuiLink>
        &apos;s MIT course site: Data Science in Context, 2024.
      </>
    ),
    note:
      'Written for MIT course 6.S963 Beyond Models — Applying Data Science/AI Effectively (Spring 2024).',
    document: AI_in_Early_Healthcare,
  },
  {
    badge: 'Paper',
    authors: [
      'Guillermo Tafoya Milo',
      'Aarón Inzunza Inzunza',
      'Javier Alejandro Banegas Moreno',
      'Juan Manuel Chávez Suazo',
    ],
    title: 'Graph-Based Embeddings for Analyzing Competency Gaps in Higher Education',
    eventOrSource:
      "Presented at Tecnológico de Monterrey's Fall 2024 TC3002B Poster Session.",
    note:
      "Written for Tecnológico de Monterrey's TC3002B: Desarrollo de Aplicaciones Avanzadas de Ciencias Computacionales.",
    document: Graph_Tec_Paper,
    poster: Graph_Tec_Poster,
  },
  {
    badge: 'Manuscript',
    authors: [
      'Guillermo Tafoya Milo',
      'Jorge Askur Vázquez Fernández',
      'Andrés Sarellano Acevedo',
      'Sebastián Joya Páez',
      'José Miguel Flores González',
      'Leonardo Garrido Luna',
      'Luis Alberto Muñoz Ubando',
    ],
    title:
      'Plataforma Tecnológica de Inteligencia Artificial para el Apoyo de Tratamiento de Personas con Trastorno del Espectro Autista',
    eventOrSource: 'Unpublished Manuscript, 2022.',
    document: SOMCIS,
  },
];

const sections = [
  { title: 'Publications', items: publications },
  { title: 'Presentations', items: presentations },
  { title: 'Written Work', items: writtenWorks },
];

const Academia = () => {
  return (
    <Box>
      <Typography variant="overline" sx={{ color: 'secondary.light', letterSpacing: 2 }}>
        Research &amp; Scholarship
      </Typography>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
        Academia
      </Typography>
      <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 680, mb: 4 }}>
        A snapshot of the papers, presentations, and manuscripts that shape my work at the
        intersection of AI, healthcare, and social impact.
      </Typography>

      <Stack spacing={4}>
        {sections.map(({ title, items }) => (
          <Box key={title}>
            <Stack spacing={1.5}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {title}
              </Typography>
              <Divider sx={{ borderColor: 'rgba(148, 163, 184, 0.18)' }} />
            </Stack>
            <Box sx={{ mt: 3 }}>
              {items.map((item, index) => (
                <AcademiaItem key={`${title}-${index}`} {...item} />
              ))}
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default Academia;
